import { useState, useEffect, type ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Wifi, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

export function AgentStatus({
  lastHeartbeat,
  isLoading = false,
}: {
  lastHeartbeat: string | null;
  isLoading?: boolean;
}): ReactElement | null {
  const timeZone = useDisplayTimeZone();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(interval);
  }, []);

  // Until the first running-now fetch resolves we don't know the heartbeat yet;
  // hide the indicator rather than flashing a false "not connected".
  if (isLoading) {
    return null;
  }

  if (!lastHeartbeat) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-muted-foreground text-sm cursor-default">
            <WifiOff className="h-3.5 w-3.5" />
            <span>Agent not connected</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">No heartbeat received from agent</div>
        </TooltipContent>
      </Tooltip>
    );
  }

  const lastSeenDate = new Date(lastHeartbeat);
  const secondsAgo = Math.floor((now - lastSeenDate.getTime()) / 1000);
  const isConnected = secondsAgo < 45;

  const formattedHeartbeat = formatDateTime(lastSeenDate, timeZone);

  if (isConnected) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-green-600 text-sm cursor-default">
            <Wifi className="h-3.5 w-3.5" />
            <span>Agent connected</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">Last heartbeat: {formattedHeartbeat}</div>
        </TooltipContent>
      </Tooltip>
    );
  }

  const timeAgo =
    secondsAgo < 60
      ? `${secondsAgo}s ago`
      : secondsAgo < 3600
        ? `${Math.floor(secondsAgo / 60)}m ago`
        : `${Math.floor(secondsAgo / 3600)}h ago`;

  const isCritical = secondsAgo > 300;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-1.5 text-sm cursor-default px-2 py-1 rounded',
            isCritical
              ? 'text-destructive bg-destructive/10 border border-destructive/30'
              : 'text-yellow-600'
          )}
        >
          <WifiOff className="h-3.5 w-3.5" />
          <span>Agent offline ({timeAgo})</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs space-y-1">
          <div>Last heartbeat: {formattedHeartbeat}</div>
          {isCritical && (
            <div className="text-destructive font-medium mt-2">
              Agent has been offline for more than 5 minutes. Process data may
              be stale.
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
